import {
  MouseEventHandler,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { Icon } from 'components/Icon';
import { VideoSize } from 'types/Video/VideoSize';
import { getVideoData } from 'utils/video';

import { VideoThumbnailContainer } from './Container';
import {
  VideoThumbnailImage,
  VideoThumbnailPlayButton,
} from './VideoThumbnail.styled';
import { VideoThumbnailSource } from './VideoThumbnailSource';
import { useVideoThumbnailUrl } from './useVideoThumbnailUrl';

type Props = {
  src: VideoThumbnailSource;
  size: VideoSize;
  playButton?: 'small' | 'large';
  onClick?: MouseEventHandler<HTMLButtonElement>;
  qaId?: string;
};

export function VideoThumbnail({
  src,
  size,
  playButton,
  onClick,
  qaId,
}: Props) {
  const videoData = useMemo(
    () =>
      src.type === 'video-thumbnail'
        ? getVideoData({ url: src.videoUrl })
        : undefined,
    [src],
  );

  const videoThumbnailUrls = useVideoThumbnailUrl({ videoData });

  const highResolutionSrc =
    src.type === 'image-url'
      ? undefined
      : videoThumbnailUrls?.highResolutionUrl;

  const [highResolutionFailed, setHighResolutionFailed] = useState(false);

  const resolvedSrc = useMemo(() => {
    if (src.type === 'image-url') return src.imageUrl;
    if (!highResolutionFailed && highResolutionSrc) return highResolutionSrc;

    return videoThumbnailUrls?.defaultResolutionUrl;
  }, [
    src,
    highResolutionFailed,
    highResolutionSrc,
    videoThumbnailUrls?.defaultResolutionUrl,
  ]);

  const onImageLoaded = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      if (highResolutionFailed || !highResolutionSrc) return;

      const img = event.target as HTMLImageElement;
      const { naturalWidth, naturalHeight } = img;

      /**
       * Some old YouTube videos don't have high quality thumbnails.
       *
       * When that happens YouTube still returns an image and `onerror` is never
       * called.
       */
      const isYoutube404Fallback =
        naturalWidth === 120 &&
        naturalHeight === 90 &&
        highResolutionSrc.startsWith('https://i.ytimg.com/vi_webp/');

      if (isYoutube404Fallback) setHighResolutionFailed(true);
    },
    [highResolutionSrc, highResolutionFailed],
  );

  return (
    <VideoThumbnailContainer size={size} onClick={onClick}>
      {resolvedSrc && (
        <VideoThumbnailImage
          src={resolvedSrc}
          alt="video thumbnail"
          data-qa-id={qaId}
          onLoad={onImageLoaded}
        />
      )}

      {playButton ? (
        <VideoThumbnailPlayButton $size={playButton}>
          <Icon name="play" size={playButton === 'small' ? 32 : 64} />
        </VideoThumbnailPlayButton>
      ) : null}
    </VideoThumbnailContainer>
  );
}
